import { isUndefined } from 'lodash';

import { getDataTypeForPoint, isNumber } from '../utils/types';
import { setDefaultValue } from '../utils/formatterUtils';
import { AbstractFormatter } from '../Formatter';
import { TypedValue, DataType } from '../DataPrimitive';
import { DataPoint } from '../DataPoint';
import EncodingExecutor from '../EncodingExecutor';

// @TODO: explore impact of stricter types here
//  - match: string | number
//  - value: string
export interface MatchesConfig {
    match: any;
    value: any;
}

/**
 * This formatter accepts a list of potential matches and a defaultValue (if no match is found) as the config.
 *
 * It maps each element in the DataSeries to find a corresponding matched value and returns the matched value if found, or the defaultValue if present.
 * Otherwise if neither are present, it returns the original value.
 *
 * The asterisk (`*`) character can be used as a wildcard in match values to represent any number of characters in a string. It can be placed anywhere within the string. Matching prioritizes exact values, followed by values with more common characters.
 * If multiple match values share the same length and number of wildcards, they will be matched according to their order of entry.
 *
 * For example, given the match values `["vis*, "*ual"]`, the string `"visual"` would match `"vis*"`.
 *
 *
 * ```js
 * <SampleViz
 *     context={{
 *         colorMatches: [
 *             {
 *                 match: 500,
 *                 value: '#FF0000',
 *             },
 *             {
 *                 match: 750,
 *                 value: '#00FF00',
 *             },
 *             {
 *                 match: "vis*",
 *                 value: '#FFFFFF',
 *             },
 *         ],
 *     }}
 *     options={{
 *         colorOption: '> primary | seriesByIndex(0) | lastPoint() | matchValue(colorMatches, "#0000FF")' // returns #FF0000
 *         colorOption2: '> primary | seriesByIndex(1) | lastPoint() | matchValue(colorMatches, "#0000FF")' // returns #0000FF
 *         colorOption3: '> primary | seriesByIndex(2) | lastPoint() | matchValue(colorMatches, "#0000FF")' // returns #FFFFFF
 *     }}
 *     dataSources={{
 *         primary: {
 *             data: {
 *                 columns: [[100, 200, 300, 400, 500], [600, 700, 800, 900, 1000], ["visual"]]
 *                 fields: [{ name: 'foo' }, { name: 'bar' }, { name: 'baz' }],
 *             }
 *         }
 *     }}
 * />
 *
 *
 *
 * @extends AbstractFormatter<DataType, DataType>
 */
export class MatchValue extends AbstractFormatter<DataType, DataType> {
    private matches: MatchesConfig[];

    private defaultValue: DataType;

    constructor(matches: MatchesConfig[], defaultValue: any = undefined) {
        super();
        this.matches = EncodingExecutor.rawTree(matches);
        this.defaultValue =
            defaultValue === undefined ? (undefined as DataType) : setDefaultValue(defaultValue);
    }

    protected formatTypedValue(input: DataPoint): TypedValue<DataType> {
        const { value } = input.getValue();

        // if no match is found, return defaultValue if present, otherwise return the original value if valid
        const defaultMatchValue = isUndefined(this.defaultValue) ? value || '' : this.defaultValue;

        // helper function to evaluate whether a value matches a wildcard pattern
        const matchWildcard = (pattern: string, val: string): boolean => {
            if (pattern === '*') return true; // handles match to wildcard should include null
            const parts = pattern.split(/\*+/); // collapse consecutive wildcards
            const firstPart = parts[0];
            const lastPart = parts[parts.length - 1];

            // check if the first and last parts match
            if (firstPart && !val.startsWith(firstPart)) return false;
            if (parts.length > 1 && lastPart && !val.endsWith(lastPart)) return false;

            let currentIndex = firstPart.length;

            for (let i = 1; i < parts.length - 1; i += 1) {
                const part = parts[i];
                // scan each part
                currentIndex = val.indexOf(part, currentIndex);
                if (currentIndex === -1) return false;
                currentIndex += part.length;
            }
            return true;
        };

        const matchNumber = (m: MatchesConfig) =>
            isNumber(value) && isNumber(m.match) && parseFloat(m.match) === parseFloat(value);

        const matchFunc = (m: MatchesConfig) => {
            // handles both match and value being 0
            if (m.match === 0 && (value === 0 || Number(value) === 0)) return true;
            // handles both match and value being null
            if (m.match === null && value === null) return true;
            if (!m || !m.match) return false;

            if (typeof m.match === 'string') {
                if (value === null && m.match !== '*') return false;
                if (m.match.includes('*')) {
                    return matchWildcard(m.match, value?.toString());
                }
                return m.match === value;
            }
            return matchNumber(m);
        };

        // helper function to calculate specificity
        const calculateSpecificity = (pattern: string): number => {
            const collapsedPattern = pattern.replace(/\*+/g, '*'); // handles consecutive wildcards, collapses into one
            const wildcardCount = (collapsedPattern.match(/\*/g) || []).length;
            // just wildcard will always have the worst specificity
            if (collapsedPattern === '*') {
                return -100;
            }
            return collapsedPattern.length - wildcardCount * 2; // fewer wildcards and longer strings have higher specificity
        };

        // find the best match without sorting
        let bestMatch = null;
        let bestSpecificity = -Infinity;
        let foundExactMatch = false;

        this.matches.forEach(m => {
            if (foundExactMatch || !matchFunc(m)) return;
            if (m.match === value) {
                bestMatch = m;
                foundExactMatch = true; // if match found exit
                return;
            }
            const specificity = calculateSpecificity(m.match);
            if (specificity > bestSpecificity) {
                bestMatch = m;
                bestSpecificity = specificity;
            }
        });

        const updatedValue = bestMatch !== null ? bestMatch.value : defaultMatchValue;
        const updatedType = getDataTypeForPoint(updatedValue);
        return { value: updatedValue, type: updatedType };
    }
}
